import { Link } from "react-router-dom";
import { useLanguage } from "@/App";
import { ArrowRight, ShieldCheck, FileText, RefreshCw, Cookie, Info, Mail, AlertTriangle, UserX } from "lucide-react";

export const LEGAL_NAV = [
  { path: "/about", ar: "من نحن", en: "About Us", icon: Info },
  { path: "/contact", ar: "اتصل بنا", en: "Contact", icon: Mail },
  { path: "/privacy", ar: "سياسة الخصوصية", en: "Privacy Policy", icon: ShieldCheck },
  { path: "/terms", ar: "شروط الاستخدام", en: "Terms of Service", icon: FileText },
  { path: "/refunds", ar: "سياسة الاسترداد", en: "Refund Policy", icon: RefreshCw },
  { path: "/disclaimer", ar: "إخلاء المسؤولية", en: "Disclaimer", icon: AlertTriangle },
  { path: "/delete-account", ar: "حذف الحساب", en: "Delete Account", icon: UserX },
  { path: "/cookies", ar: "سياسة الكوكيز", en: "Cookie Policy", icon: Cookie },
];

/**
 * Reusable legal/info page shell. Renders standalone (works without auth).
 */
export default function LegalLayout({ titleAr, titleEn, lastUpdated = "February 2026", children }) {
  const { t, language, toggleLanguage } = useLanguage();
  return (
    <div className="min-h-screen bg-slate-950 text-white" dir={language === "ar" ? "rtl" : "ltr"}>
      {/* Top bar */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur sticky top-0 z-30">
        <div className="container-app py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="legal-home-link">
            <div className="w-10 h-10 bg-yellow-500 rounded-xl flex items-center justify-center">
              <span className="text-black font-bold text-lg">س</span>
            </div>
            <span className="text-xl font-bold">Arab Sanad</span>
          </Link>
          <button onClick={toggleLanguage} className="text-sm text-slate-300 hover:text-yellow-400" data-testid="legal-lang-toggle">
            {language === "ar" ? "EN" : "AR"}
          </button>
        </div>
      </header>

      <div className="container-app py-10 grid lg:grid-cols-[260px_1fr] gap-8">
        {/* Side nav */}
        <aside className="lg:sticky lg:top-24 self-start">
          <div className="glass-card rounded-2xl p-3 space-y-1">
            {LEGAL_NAV.map((it) => (
              <Link
                key={it.path}
                to={it.path}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                data-testid={`legal-nav-${it.path.slice(1)}`}
              >
                <it.icon size={16} className="text-yellow-500" />
                {language === "ar" ? it.ar : it.en}
              </Link>
            ))}
          </div>
        </aside>

        {/* Content */}
        <main className="glass-card rounded-2xl p-6 lg:p-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-yellow-500 hover:underline mb-4">
            <ArrowRight size={14} /> {t("الصفحة الرئيسية", "Back to Home")}
          </Link>
          <h1 className="text-3xl lg:text-4xl font-bold mb-2 text-luxury">{language === "ar" ? titleAr : titleEn}</h1>
          <p className="text-sm text-slate-500 mb-8">{t("آخر تحديث:", "Last updated:")} {lastUpdated}</p>
          <div className="legal-prose space-y-5 text-slate-300 leading-relaxed">
            {children}
          </div>
        </main>
      </div>

      <footer className="border-t border-slate-800 mt-10 py-6 text-center text-xs text-slate-500">
        © 2026 Arab Sanad · {t("جميع الحقوق محفوظة", "All rights reserved")}
      </footer>
    </div>
  );
}
